// src/components/views/DesignerGateView.tsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { 
  ArrowLeft, Search, ShoppingBag, Bell, MessageCircle, 
  Plus, Filter, Heart, X, User, Loader2, Share2, 
  Link as LinkIcon, Users, Star, StarHalf, Award, 
  Package, Flame, Minus, Plus as PlusIcon, Grid, List, 
  Shirt, Scissors, PackageOpen, Footprints, 
  ScrollText as FabricIcon, Tag as TagIcon, Compass, ShoppingBag as FinishedGoods,
  MapPin, Clock, ChevronLeft, ChevronRight, TrendingUp, MessageSquare, Zap,
  Factory, Palette, Ruler, Check, Send, FileText, Gem
} from 'lucide-react';
import { db } from '../../services/firebase';
import { 
  collection, query, where, orderBy, onSnapshot, doc, getDoc, updateDoc, 
  arrayUnion, arrayRemove, serverTimestamp, setDoc, addDoc, increment, getDocs
} from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { DesignerProductDetailModal } from '../shopping/DesignerProductDetailModal';
import { DesignerProfileModal } from '../shopping/DesignerProfileModal';
import { EnquiryModal } from '../shopping/EnquiryModal';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface DesignerItem {
  id: string;
  name: string;
  price: number;
  priceMin?: number;
  priceMax?: number;
  image: string;
  images?: string[];
  category: string;
  description: string;
  sellerId: string;
  sellerName: string;
  sellerAvatar?: string;
  sellerCompany?: string;
  sellerLocation?: string;
  sellerJoined?: string;
  supplierType: 'gold' | 'silver' | 'standard';
  rating: number;
  reviewCount: number;
  soldCount: number;
  reorderRate: number;
  isHot: boolean;
  isNew: boolean;
  discount?: number;
  moq: number;
  tags: string[];
  specs: Record<string, string>;
  customizationOptions: Array<{ name: string; minOrder: number; pricePerUnit: number }>;
  keyAttributes: Record<string, string>;
  likes?: string[];
  comments?: number;
  createdAt: any;
  colors?: string[];
  sizes?: string[];
  deals?: Array<{ quantity: string; price: string; label: string }>;
  media?: Array<{ type: 'image' | 'video'; url: string }>;
  workspaceId?: string;
  businessId?: string;
  uid?: string;
  photoURL?: string;
  logoUrl?: string;
  location?: string;
  businessName?: string;
  leadTime?: string;
}

interface DesignerGateViewProps {
  wishlist?: string[];
  onToggleWishlist?: (id: string, type: 'product') => void;
  onSelectProduct?: (product: DesignerItem) => void;
  onOpenSellModal?: () => void;
  onViewProfile?: (userId: string) => void;
  onSendMessage?: (sellerId: string, sellerName: string) => void;
}

interface FilterState {
  minPrice: number;
  maxPrice: number;
  moq: number;
  rating: number;
  category: string;
  location: string;
  materials: string[];
}

interface Category {
  name: string;
  icon: React.ComponentType<any>;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

// Category Card - Fixed Size
const CategoryCard: React.FC<{
  category: string;
  icon: React.ComponentType<any>;
  onClick: () => void;
  isActive: boolean;
}> = ({ category, icon: Icon, onClick, isActive }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center justify-center rounded-lg p-4 transition-all duration-200 border w-full h-[100px] lg:h-[110px] ${
      isActive 
        ? 'bg-orange-500 text-white border-orange-500' 
        : 'bg-white text-gray-700 border-gray-200 hover:border-orange-400 hover:bg-orange-50'
    }`}
  >
    <Icon size={28} className={`mb-2 ${isActive ? 'text-white' : 'text-orange-500'}`} strokeWidth={1.5} />
    <span className="text-[10px] lg:text-[11px] font-bold uppercase tracking-tight text-center leading-tight">
      {category}
    </span>
  </button>
);

// Deal Badge
const DealBadge: React.FC<{ deal: { quantity: string; price: string; label: string } }> = ({ deal }) => (
  <div className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500 text-white text-[9px] font-bold">
    <TagIcon size={10} />
    {deal.quantity} for R{deal.price}
    {deal.label && <span className="ml-1 opacity-80">{deal.label}</span>}
  </div>
);

// ============================================================================
// PRODUCT CARD
// ============================================================================

const ProductCard: React.FC<{
  item: DesignerItem;
  isWishlisted: boolean;
  onToggleWishlist?: (id: string, type: 'product') => void;
  onClick: () => void;
  onEnquire: (item: DesignerItem) => void;
}> = ({ item, isWishlisted, onToggleWishlist, onClick, onEnquire }) => {
  const bestDeal = item.deals?.length ? item.deals[0] : null;
  const colorCount = item.colors?.length || 0;
  const sizeCount = item.sizes?.length || 0;
  const sellerDisplayName = item.sellerCompany || item.sellerName || item.businessName || 'Studio';

  return (
    <div className="mb-4 lg:mb-6 bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-orange-400 transition-all duration-200 cursor-pointer group">
      {/* Image */}
      <div 
        className="relative aspect-square bg-gray-50 overflow-hidden"
        onClick={onClick}
      >
        <img 
          src={item.image || item.images?.[0] || 'https://placehold.co/600x600/eee/999?text=No+Image'} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          alt={item.name} 
        />
        
        {item.isHot && (
          <span className="absolute top-3 left-3 bg-orange-500 text-white text-[10px] font-bold px-2.5 py-1 flex items-center gap-1">
            <Flame size={12} /> HOT
          </span>
        )}
        
        {bestDeal && (
          <div className="absolute bottom-3 left-3">
            <DealBadge deal={bestDeal} />
          </div>
        )}
        
        <button
          onClick={(e) => { e.stopPropagation(); onToggleWishlist?.(item.id, 'product'); }}
          className="absolute top-3 right-3 p-2 bg-white border border-gray-200 hover:border-orange-400 transition-all"
        >
          <Heart size={18} fill={isWishlisted ? 'currentColor' : 'none'} className={isWishlisted ? 'text-orange-500' : 'text-gray-400'} />
        </button>
        
        {(colorCount > 0 || sizeCount > 0) && (
          <div className="absolute bottom-3 right-3 bg-black/80 px-2 py-1 flex items-center gap-1.5">
            {colorCount > 0 && (
              <div className="flex items-center gap-1">
                <Palette size={10} className="text-white/60" />
                <span className="text-[9px] font-bold text-white">{colorCount}</span>
              </div>
            )}
            {colorCount > 0 && sizeCount > 0 && (
              <div className="w-px h-3 bg-white/20" />
            )}
            {sizeCount > 0 && (
              <div className="flex items-center gap-1">
                <Ruler size={10} className="text-white/60" />
                <span className="text-[9px] font-bold text-white">{sizeCount}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-4">
        <div className="flex justify-between items-start mb-1">
          <p className="text-[9px] font-bold uppercase text-gray-500 bg-gray-100 px-2 py-0.5">
            {item.category}
          </p>
          <div className="flex items-center gap-1">
            <Star size={10} className="fill-orange-400 text-orange-400" />
            <span className="text-[10px] font-bold text-gray-700">{item.rating || 4.5}</span>
          </div>
        </div>
        
        <h3 className="font-bold text-sm text-gray-900 line-clamp-2 mb-1 group-hover:text-orange-500 transition-colors leading-snug">
          {item.name}
        </h3>
        
        {/* Seller */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-full bg-gray-200 border border-gray-300 overflow-hidden shrink-0">
            {item.sellerAvatar || item.photoURL || item.logoUrl ? (
              <img 
                src={item.sellerAvatar || item.photoURL || item.logoUrl} 
                className="w-full h-full object-cover" 
                alt={sellerDisplayName} 
              />
            ) : (
              <Factory size={12} className="m-auto mt-1 text-gray-500" />
            )}
          </div>
          <span className="text-[10px] text-gray-600 truncate font-medium">
            {sellerDisplayName}
          </span>
        </div>

        {/* Price & MOQ */}
        <div className="flex items-end justify-between pt-3 border-t border-gray-200">
          <div>
            {item.priceMax && item.priceMax > item.price && (
              <p className="text-[10px] text-gray-400 line-through mb-0.5">R{item.priceMax.toFixed(2)}</p>
            )}
            <p className="text-lg font-bold text-orange-600 leading-none">R{item.price.toFixed(2)}</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-gray-500 uppercase tracking-widest font-bold mb-1">Min. Order</p>
            <p className="text-sm font-bold text-gray-900">{item.moq} pcs</p>
          </div>
        </div>

        {/* Enquire Button */}
        <button
          onClick={(e) => { e.stopPropagation(); onEnquire(item); }}
          className="w-full mt-3 py-2.5 bg-orange-500 text-white font-bold text-[10px] uppercase tracking-widest hover:bg-orange-600 transition-all flex items-center justify-center gap-2"
        >
          <Send size={12} />
          Enquire
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DesignerGateView: React.FC<DesignerGateViewProps> = ({
  wishlist = [],
  onToggleWishlist,
  onSendMessage,
  onSelectProduct: parentOnSelectProduct,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, profile: userProfile } = useAuth();
  
  // ============================================================================
  // STATE
  // ============================================================================
  const [items, setItems] = useState<DesignerItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ 
    minPrice: 0, 
    maxPrice: 100000, 
    moq: 0, 
    rating: 0,
    category: 'all',
    location: '',
    materials: [],
  });
  const [selectedItem, setSelectedItem] = useState<DesignerItem | null>(null);
  const [viewingDesigner, setViewingDesigner] = useState<any>(null);
  const [designerProducts, setDesignerProducts] = useState<any[]>([]);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // Enquiry Modal State
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<DesignerItem | null>(null);
  const [enquirySuccess, setEnquirySuccess] = useState(false);

  // ============================================================================
  // CONSTANTS
  // ============================================================================
  const categories: Category[] = [
    { name: 'All', icon: Compass },
    { name: 'Cut & Sew', icon: Scissors },
    { name: 'Screen Printing', icon: Shirt },
    { name: 'Embroidery', icon: FabricIcon },
    { name: 'Blanks', icon: FinishedGoods },
    { name: 'Fabrics', icon: FabricIcon },
    { name: 'Full Package (CMT)', icon: Package },
    { name: 'Footwear', icon: Footprints },
  ];

  const breakpointColumns = { 
    default: 4, 
    1280: 4, 
    1024: 3, 
    768: 2, 
    500: 2 
  };

  // ============================================================================
  // DERIVED DATA
  // ============================================================================
  const isWishlisted = useCallback((id: string) => wishlist.includes(id), [wishlist]);
  
  // Cart count
  const cartCount = 0; // Placeholder

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const searchStr = searchTerm.toLowerCase();
      const matchesSearch = 
        item.name?.toLowerCase().includes(searchStr) || 
        item.sellerCompany?.toLowerCase().includes(searchStr) || 
        item.businessName?.toLowerCase().includes(searchStr) ||
        item.description?.toLowerCase().includes(searchStr);
      
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      const matchesPrice = item.price >= filters.minPrice && item.price <= filters.maxPrice;
      const matchesMOQ = item.moq >= filters.moq;
      const matchesRating = (item.rating || 0) >= filters.rating;
      
      return matchesSearch && matchesCategory && matchesPrice && matchesMOQ && matchesRating;
    });
  }, [items, searchTerm, activeCategory, filters]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  useEffect(() => {
    const q = query(
      collection(db, 'products'),
      where('workspaceType', '==', 'designer'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      } as DesignerItem));
      setItems(data);
      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching designer products:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleProductClick = useCallback((item: DesignerItem) => {
    setSelectedItem(item);
  }, []);

  const handleEnquire = useCallback((item: DesignerItem) => {
    setSelectedManufacturer(item);
    setShowEnquiryModal(true);
  }, []);

  const handleEnquirySent = useCallback(() => {
    setEnquirySuccess(true);
    setTimeout(() => setEnquirySuccess(false), 3000);
  }, []);

  const handleViewProfile = useCallback(async (id: string) => {
    if (!id) {
      alert('Unable to load profile: No ID provided');
      return;
    }
    
    setIsProfileLoading(true);
    try {
      let brandData = null;
      let productsData: any[] = [];
      
      const userDoc = await getDoc(doc(db, 'users', id));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        brandData = { 
          id: userDoc.id,
          uid: userDoc.id,
          businessName: userData.businessName || userData.displayName || userData.username || 'Studio',
          displayName: userData.displayName || userData.businessName || userData.username || 'Studio',
          name: userData.businessName || userData.displayName || userData.username || 'Studio',
          location: userData.location || userData.sellerLocation || 'Global',
          photoURL: userData.photoURL || userData.logoUrl || userData.sellerAvatar || null,
          logoUrl: userData.logoUrl || userData.photoURL || userData.sellerAvatar || null,
          bio: userData.bio || userData.description || '',
          description: userData.description || userData.bio || '',
          verified: userData.verified || false,
          followersCount: userData.followersCount || userData.followers || 0,
          ...userData
        };
      }
      
      // Search by workspaceId in products
      if (!brandData) {
        const productQuery = query(collection(db, 'products'), where('workspaceId', '==', id));
        const productSnap = await getDocs(productQuery);
        
        if (!productSnap.empty()) {
          const firstProduct = productSnap.docs[0].data();
          productsData = productSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          
          brandData = {
            id: id,
            uid: id,
            businessName: firstProduct.sellerCompany || firstProduct.sellerName || 'Studio',
            displayName: firstProduct.sellerName || firstProduct.sellerCompany || 'Studio',
            name: firstProduct.sellerCompany || firstProduct.sellerName || 'Studio',
            location: firstProduct.sellerLocation || 'Global',
            photoURL: firstProduct.sellerAvatar || null,
            logoUrl: firstProduct.sellerAvatar || null,
            bio: firstProduct.description || '',
            description: firstProduct.description || '',
          };
        }
      }
      
      if (brandData) {
        setViewingDesigner(brandData);
        setDesignerProducts(productsData);
      } else {
        alert('This studio profile could not be found.');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      alert('Error loading profile. Please try again.');
    } finally {
      setIsProfileLoading(false);
    }
  }, []);

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  if (isLoading && items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-orange-500" size={40} />
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="min-h-screen bg-gray-50 text-black font-sans pb-24 relative">
      
      {/* =========================================================================
          🖥️ DESKTOP HEADER - FIXED GAMES TAB
      ========================================================================= */}
      <header className="hidden lg:flex fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 flex-col">
        
        {/* Top Bar */}
        <div className="flex justify-between items-center py-2 px-8 border-b border-gray-200 text-[10px] uppercase tracking-widest font-bold text-gray-500">
          <span>Designer Gate</span>
          <span>B2B Wholesale & Manufacturing</span>
        </div>

        {/* Main Header */}
        <div className="flex items-center justify-between h-20 px-8">
          {/* Left Nav - Brands | Jewellery | Thrift | Games | Designer | Collections | Community */}
          <nav className="flex gap-6 items-center">
            {['Brands', 'Jewellery', 'Thrift', , 'Designer', ].map((link) => {
              let path = '/';
              if (link === 'Brands') path = '/';
              else if (link === 'Jewellery') path = '/jewellery';
              else if (link === 'Thrift') path = '/thrifts';
              else if (link === 'Games') path = '/games';  // ✅ FIXED
              else if (link === 'Designer') path = '/designer';
              
              return (
                <NavLink 
                  key={link} 
                  to={path}
                  className={({isActive}) => `text-xs font-bold uppercase tracking-[0.2em] hover:text-orange-500 transition-colors whitespace-nowrap ${isActive ? 'text-orange-500 border-b-2 border-orange-500 pb-1' : 'text-gray-500'}`}
                >
                  {link}
                </NavLink>
              );
            })}
          </nav>

          {/* Center Logo - Designer Gate */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Factory size={24} className="text-orange-500" />
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Designer <span className="font-light">Gate</span>
            </h1>
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-6">
            <NavLink to="/search" className="text-gray-500 hover:text-orange-500 transition-colors" title="Search">
              <Search size={20} />
            </NavLink>
            
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-messages'))} className="text-gray-500 hover:text-orange-500 transition-colors relative" title="Messages">
              <MessageCircle size={20} />
            </button>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))} className="text-gray-500 hover:text-orange-500 transition-colors relative" title="Notifications">
              <Bell size={20} />
            </button>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-creation-menu'))} className="flex items-center gap-2 text-gray-500 hover:text-orange-500 transition-colors" title="Create Post">
              <Plus size={20} strokeWidth={2} />
            </button>

            <NavLink to="/profile" className="text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-orange-500 transition-colors">
              {currentUser ? `Account` : 'Login'}
            </NavLink>
            
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-cart'))} className={`flex items-center gap-2 transition-colors ${cartCount > 0 ? 'text-orange-500' : 'text-gray-500 hover:text-orange-500'}`}>
              <ShoppingBag size={20} />
              <span className="text-xs font-bold">({cartCount})</span>
            </button>
          </div>
        </div>
      </header>

      {/* =========================================================================
          📱 MOBILE HEADER - FIXED GAMES TAB
      ========================================================================= */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200">
        {/* Top Row - Logo and Icons */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Factory size={20} className="text-orange-500" />
            <h1 className="text-lg font-bold text-black">
              Designer <span className="font-light">Gate</span>
            </h1>
          </div>
          
          <div className="flex gap-4 items-center text-gray-500">
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-messages'))} className="hover:text-orange-500 transition-colors relative">
              <MessageCircle size={20} />
            </button>
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))} className="hover:text-orange-500 transition-colors relative">
              <Bell size={20} />
            </button>
            
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-cart'))} className="hover:text-orange-500 transition-colors relative">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Tabs - Order: Brands, Jewellery, Thrift, Games, Designer */}
        <div className="flex overflow-x-auto scrollbar-hide border-t border-gray-200">
          {['Brands', 'Jewellery', 'Thrift', , 'Designer'].map((tab) => {
            let path = '/';
            let isActive = false;
            
            if (tab === 'Brands') { 
              path = '/'; 
              isActive = location.pathname === '/'; 
            }
            else if (tab === 'Jewellery') { 
              path = '/jewellery'; 
              isActive = location.pathname === '/jewellery'; 
            }
            else if (tab === 'Thrift') { 
              path = '/thrifts'; 
              isActive = location.pathname === '/thrifts'; 
            }
            else if (tab === 'Games') { 
              path = '/games';  // ✅ FIXED
              isActive = location.pathname === '/games'; 
            }
            else if (tab === 'Designer') { 
              path = '/designer'; 
              isActive = location.pathname === '/designer'; 
            }
            
            return (
              <NavLink
                key={tab}
                to={path}
                className={({isActive: isNavActive}) => {
                  const active = isNavActive || isActive;
                  return `flex-1 text-center px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                    active ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-400 hover:text-gray-700'
                  }`;
                }}
              >
                {tab}
              </NavLink>
            );
          })}
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-4 pt-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              placeholder="Search manufacturers, blanks, tech packs..."
              className="w-full bg-gray-100 border border-gray-200 rounded-lg py-2.5 pl-10 pr-3 text-sm font-medium outline-none focus:border-orange-400 focus:bg-white text-black placeholder:text-gray-400 transition-all"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>

      {/* =========================================================================
          MAIN CONTENT - With top padding for desktop header
      ========================================================================= */}
      <div className="lg:pt-[105px] pt-0">
        <div className="px-4 sm:px-6 max-w-7xl mx-auto pt-4">
          
          {/* Categories - Fixed Size */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6 mb-8">
            <div className="grid grid-cols-4 lg:grid-cols-7 gap-3">
              {categories.map(cat => (
                <CategoryCard
                  key={cat.name}
                  category={cat.name}
                  icon={cat.icon}
                  isActive={activeCategory === cat.name}
                  onClick={() => setActiveCategory(cat.name)}
                />
              ))}
            </div>
          </div>

          {/* Hero Banner - With Orange */}
          <div className="hidden lg:flex bg-black rounded-lg overflow-hidden mb-8 border border-black relative">
            <div className="p-12 z-10 w-3/5">
              <span className="bg-orange-500 text-white font-bold tracking-widest text-[10px] uppercase px-3 py-1 inline-block mb-4">
                Factory Direct
              </span>
              <h2 className="text-4xl font-bold text-white leading-tight mb-4">
                Build Your Brand.<br/>Scale Your Production.
              </h2>
              <p className="text-gray-400 mb-8 max-w-md text-sm">
                Source premium streetwear blanks, connect with verified cut & sew manufacturers, and order custom packaging.
              </p>
              <div className="flex gap-4">
                <button className="bg-orange-500 text-white px-8 py-3.5 font-bold hover:bg-orange-600 transition-colors">
                  Explore Factories
                </button>
                <button className="border border-white text-white px-8 py-3.5 font-bold hover:bg-white/10 transition-colors">
                  View Blank Catalog
                </button>
              </div>
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-2/5 flex items-center justify-end overflow-hidden">
              <div className="w-96 h-96 bg-orange-500/20 rounded-full blur-3xl absolute right-10"></div>
              <Factory size={200} className="text-white/5 absolute -right-10 transform rotate-12" />
            </div>
          </div>

          {/* Filter Bar */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {activeCategory === 'All' ? 'Latest Sourcing Deals' : activeCategory}
            </h2>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 hover:border-orange-400 text-xs font-bold uppercase transition-all text-gray-700 hover:text-orange-500"
            >
              <Filter size={16} /> Filter
              {(filters.minPrice > 0 || filters.moq > 0 || filters.rating > 0) && (
                <span className="w-2 h-2 rounded-full bg-orange-500 ml-1"></span>
              )}
            </button>
          </div>

          {/* Product Grid */}
          {filteredItems.length === 0 ? (
            <div className="text-center py-24 bg-white rounded-lg border border-gray-200">
              <Package className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-900 font-bold text-lg mb-1">No products found</p>
              <p className="text-gray-500 text-sm">Try adjusting your filters or search terms.</p>
            </div>
          ) : (
            <Masonry 
              breakpointCols={breakpointColumns} 
              className="my-masonry-grid" 
              columnClassName="my-masonry-grid_column"
            >
              {filteredItems.map(item => (
                <ProductCard
                  key={item.id}
                  item={item}
                  isWishlisted={isWishlisted(item.id)}
                  onToggleWishlist={onToggleWishlist}
                  onClick={() => handleProductClick(item)}
                  onEnquire={handleEnquire}
                />
              ))}
            </Masonry>
          )}
        </div>
      </div>

      {/* =========================================================================
          MODALS
      ========================================================================= */}

      {/* Loading Overlay */}
      {isProfileLoading && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50">
          <Loader2 className="animate-spin text-orange-500" size={48} />
        </div>
      )}

      {/* Enquiry Modal */}
      <EnquiryModal
        isOpen={showEnquiryModal}
        onClose={() => {
          setShowEnquiryModal(false);
          setSelectedManufacturer(null);
        }}
        manufacturer={selectedManufacturer ? {
          id: selectedManufacturer.sellerId || selectedManufacturer.uid || selectedManufacturer.id,
          businessName: selectedManufacturer.sellerCompany || selectedManufacturer.businessName || selectedManufacturer.sellerName || 'Studio',
          displayName: selectedManufacturer.sellerName || selectedManufacturer.displayName || 'Studio',
          photoURL: selectedManufacturer.sellerAvatar || selectedManufacturer.photoURL || selectedManufacturer.logoUrl,
          logoUrl: selectedManufacturer.logoUrl || selectedManufacturer.sellerAvatar || selectedManufacturer.photoURL,
          location: selectedManufacturer.sellerLocation || selectedManufacturer.location || 'Global',
          moq: selectedManufacturer.moq,
          leadTime: selectedManufacturer.leadTime,
        } : null}
        onEnquirySent={handleEnquirySent}
      />

      {/* Product Detail Modal */}
      <DesignerProductDetailModal
        item={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onToggleWishlist={onToggleWishlist}
        isWishlisted={selectedItem ? isWishlisted(selectedItem.id) : false}
        userProfile={userProfile}
        currentUser={currentUser}
        onViewProfile={handleViewProfile}
        onSendMessage={onSendMessage}
      />

      {/* Profile Modal */}
      <DesignerProfileModal 
        isOpen={!!viewingDesigner}
        onClose={() => {
          setViewingDesigner(null);
          setDesignerProducts([]);
        }}
        brand={viewingDesigner}
        products={designerProducts}
        onSelectProduct={(product: any) => {
          setSelectedItem(product);
        }}
        onSendMessage={onSendMessage}
      />

      {/* =========================================================================
          FILTER MODAL
      ========================================================================= */}
      {isFilterOpen && (
        <div className="fixed inset-0 z-[1000] flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/50 animate-in fade-in" onClick={() => setIsFilterOpen(false)} />
          <div className="bg-white w-full max-w-lg mx-auto rounded-t-2xl p-6 pb-safe relative z-10 animate-in slide-in-from-bottom-full duration-300 border-t border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Filters</h2>
              <button onClick={() => setIsFilterOpen(false)} className="p-2 bg-gray-100 hover:bg-gray-200 transition-colors">
                <X size={20}/>
              </button>
            </div>
            
            {/* Price Range */}
            <div className="mb-6">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-3">Price Range (ZAR)</h3>
              <div className="flex gap-4 items-center">
                <input 
                  type="number" 
                  value={filters.minPrice} 
                  onChange={(e) => setFilters({...filters, minPrice: Number(e.target.value) || 0})} 
                  className="w-full p-3 bg-gray-50 border border-gray-200 text-sm font-bold outline-none focus:border-orange-400" 
                  placeholder="Min" 
                />
                <span className="text-gray-400 font-bold">-</span>
                <input 
                  type="number" 
                  value={filters.maxPrice} 
                  onChange={(e) => setFilters({...filters, maxPrice: Number(e.target.value) || 0})} 
                  className="w-full p-3 bg-gray-50 border border-gray-200 text-sm font-bold outline-none focus:border-orange-400" 
                  placeholder="Max" 
                />
              </div>
            </div>

            {/* MOQ */}
            <div className="mb-8">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-3">Min. Order Quantity (MOQ)</h3>
              <input 
                type="number" 
                value={filters.moq} 
                onChange={(e) => setFilters({...filters, moq: Number(e.target.value) || 0})} 
                className="w-full p-3 bg-gray-50 border border-gray-200 text-sm font-bold outline-none focus:border-orange-400" 
                placeholder="e.g. 50" 
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button 
                onClick={() => setFilters({minPrice: 0, maxPrice: 100000, moq: 0, rating: 0, category: 'all', location: '', materials: []})} 
                className="w-1/3 py-4 font-bold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Reset
              </button>
              <button 
                onClick={() => setIsFilterOpen(false)} 
                className="w-2/3 bg-orange-500 text-white font-bold text-sm py-4 hover:bg-orange-600 transition-all"
              >
                Show {filteredItems.length} Results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for Masonry */}
      <style>{`
        .my-masonry-grid {
          display: flex;
          margin-left: -16px;
          width: auto;
        }
        .my-masonry-grid_column {
          padding-left: 16px;
          background-clip: padding-box;
        }
        .my-masonry-grid_column > div {
          margin-bottom: 16px;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default DesignerGateView;